/**
 * Param names that can carry a Stripe client secret in a dashboard URL.
 *
 * Stripe appends the first two to the return_url on a redirect-based confirmation
 * and that append can't be disabled. `redirect: "if_required"` keeps card and bank
 * debit in-page, so only genuinely redirect-based methods land here.
 *
 * The camelCase three are the checkout route's former search params. Nothing
 * generates them now, but a bookmark or still-open tab from before that change
 * carries them, both in its own URL and as the Referer on its next navigation.
 */
export const STRIPE_SECRET_URL_PARAMS = [
  "payment_intent_client_secret",
  "setup_intent_client_secret",
  "customerSessionSecret",
  "paymentIntentSecret",
  "setupIntentSecret",
] as const;

const REDACTED = "[redacted]";

const STRIPE_SECRET_PARAM_PATTERN = new RegExp(
  `([?&#](?:${STRIPE_SECRET_URL_PARAMS.join("|")})=)[^&#]*`,
  "gi"
);

/** Returns `href` with the Stripe secret params removed, or null if there were none. */
export const stripStripeSecretParams = (href: string): string | null => {
  const url = new URL(href);
  let removed = false;
  for (const param of STRIPE_SECRET_URL_PARAMS) {
    if (url.searchParams.has(param)) {
      url.searchParams.delete(param);
      removed = true;
    }
  }
  return removed ? url.href : null;
};

/**
 * Replaces the value of any Stripe secret param inside a string, keeping the rest
 * of the URL so telemetry can still group by route.
 *
 * The stripping in `main.tsx` runs before the analytics SDKs initialize, but that
 * ordering isn't a guarantee: `index.html` already loads a third-party script
 * ahead of the app bundle. This redactor runs inside each SDK's send hook, so it
 * holds no matter when the SDK was set up, and it is the only thing that can reach
 * a secret carried in `document.referrer` by a tab predating the strip.
 */
export const redactStripeSecretParams = (value: string): string =>
  value.replace(STRIPE_SECRET_PARAM_PATTERN, `$1${REDACTED}`);
